// Pure function: effective-config subset -> runtime limits the runner needs.
// §18 + plan.md D2: operational numbers live in config, not code. Defaults
// below are the DOCUMENTED defaults (§18/D2) applied only when a field is
// absent from the passed-in config — this is not "inventing a number", it's
// the same default the config loader (P5) is supposed to snapshot.
//
// `timeouts_min[kind]` is the one exception: the phase file is explicit that
// there is NO safe universal default for a per-kind deadline (a "quick fix"
// kind and a "large refactor" kind cannot share a guess). Missing kind =
// throw. Do not add a fallback number here even for convenience — that is
// the deliberate strictness D2 asks for.

// Minimal local type for the config subset this file needs. Does NOT import
// from P4/P5 modules (they don't exist yet in this phase-subset).
export interface EffectiveConfigLimitsSubset {
  startup_timeout_sec?: number;
  shutdown_grace_sec?: number;
  lease_stale_sec?: number;
  timeouts_min?: Record<string, number>;
  limits?: {
    prompt_max_kib?: number;
    line_max_mib?: number;
    raw_log_max_mib?: number;
    stderr_ring_kib?: number;
  };
}

export interface RuntimeLimits {
  startupTimeoutMs: number;
  deadlineMs: number;
  graceMs: number;
  leaseStaleSec: number;
  limits: {
    promptMaxKib: number;
    lineMaxMib: number;
    rawLogMaxMib: number;
    stderrRingKib: number;
  };
}

const DEFAULT_STARTUP_TIMEOUT_SEC = 120;
export const DEFAULT_SHUTDOWN_GRACE_SEC = 20;
const DEFAULT_LEASE_STALE_SEC = 60;
const DEFAULT_PROMPT_MAX_KIB = 512;
const DEFAULT_LINE_MAX_MIB = 1;
const DEFAULT_RAW_LOG_MAX_MIB = 64;
const DEFAULT_STDERR_RING_KIB = 64;

export function runtimeLimitsFromEffectiveConfig(
  config: EffectiveConfigLimitsSubset,
  kind: string,
): RuntimeLimits {
  const timeoutsMin = config.timeouts_min ?? {};
  const kindMinutes = timeoutsMin[kind];
  if (kindMinutes === undefined) {
    throw new Error(
      `runtimeLimitsFromEffectiveConfig: thiếu timeouts_min["${kind}"] trong effective-config. ` +
        "Không có default an toàn cho ngưỡng deadline theo kind (§18/D2) — phải khai rõ trong config.",
    );
  }

  const startupTimeoutSec = config.startup_timeout_sec ?? DEFAULT_STARTUP_TIMEOUT_SEC;
  const shutdownGraceSec = config.shutdown_grace_sec ?? DEFAULT_SHUTDOWN_GRACE_SEC;
  const leaseStaleSec = config.lease_stale_sec ?? DEFAULT_LEASE_STALE_SEC;
  const limits = config.limits ?? {};

  return {
    startupTimeoutMs: startupTimeoutSec * 1000,
    deadlineMs: kindMinutes * 60_000,
    graceMs: shutdownGraceSec * 1000,
    leaseStaleSec,
    limits: {
      promptMaxKib: limits.prompt_max_kib ?? DEFAULT_PROMPT_MAX_KIB,
      lineMaxMib: limits.line_max_mib ?? DEFAULT_LINE_MAX_MIB,
      rawLogMaxMib: limits.raw_log_max_mib ?? DEFAULT_RAW_LOG_MAX_MIB,
      stderrRingKib: limits.stderr_ring_kib ?? DEFAULT_STDERR_RING_KIB,
    },
  };
}
